import { getBrowserTimeZoneOffset } from "../../../../utils/format";
import { getWafSourceDisplayLabel } from "./wafSourceLabels";

/** Default custom range for WAF events — rolling last 30 days through today, local time. */
export function getDefaultWafCustomDateRange() {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  const fmt = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  return [fmt(start), fmt(end)];
}

/** Picker confirm value → `YYYY-MM-DDTHH:mm:ss` in the user's local wall clock. */
export function normalizePickerDateTime(part) {
  if (!part || typeof part !== "string") return part;
  const trimmed = part.trim();
  if (trimmed.includes("T")) {
    return trimmed.length === 16 ? `${trimmed}:00` : trimmed;
  }
  const [date, time] = trimmed.split(" ");
  const t = time || "00:00:00";
  const withSec = t.length === 5 ? `${t}:00` : t;
  return `${date}T${withSec}`;
}

/** RangePicker onConfirm payload → `[from, to]` storage strings. */
export function normalizePickerDateRange(res) {
  if (!Array.isArray(res) || res.length < 2) return res;
  return [normalizePickerDateTime(res[0]), normalizePickerDateTime(res[1])];
}

/** UTC/ISO API timestamp → local picker/storage format. */
export function isoToLocalPickerDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** Custom range for WAF APIs — naive local from/to + browser UTC offset. */
export function buildCustomWafTimeRange(from, to) {
  return normalizeWafEventTimeRange({
    time_zone: getBrowserTimeZoneOffset(),
    from,
    to,
  });
}

/** Map list row → display status (Blocked / Challenged / Passed). Shared by WAF + Firewall tables. */
export function getWafEventStatus(item) {
  if (!item) return "Passed";

  const action = String(item.action_taken || item.waf_action || item.action || "").toLowerCase();
  if (action === "challenge" || action === "challenged") return "Challenged";
  if (action === "block" || action === "blocked") return "Blocked";
  if (item.blocked === 1 || item.blocked === true) return "Blocked";
  if (
    action === "detect" ||
    action === "detected" ||
    action === "passed" ||
    action === "log" ||
    action === "logged" ||
    action === "allow"
  ) {
    return "Passed";
  }

  const rawStatus = item.status ?? item.resStatus ?? item.res_status;
  const httpStatus = Number(rawStatus);
  if (Number.isFinite(httpStatus) && httpStatus > 0) {
    if (httpStatus === 403 || httpStatus === 429 || httpStatus === 301) return "Blocked";
    if (httpStatus === 503) return "Challenged";
    return "Passed";
  }
  return "Passed";
}

/** Collect attack type strings from a log/waf_event list row. */
export function getWafEventAttackTypes(item) {
  if (!item) return [];
  if (Array.isArray(item.attack_types)) {
    return item.attack_types.map((t) => String(t).trim()).filter(Boolean);
  }
  if (typeof item.attack_types === "string" && item.attack_types.trim()) {
    return item.attack_types
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }
  if (item.attack_type != null && String(item.attack_type).trim()) {
    return String(item.attack_type)
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
}

/** Humanize slug attack types (e.g. shell-injection → Shell Injection). */
export function formatWafAttackTypeLabel(value) {
  const s = String(value ?? "").trim();
  if (!s) return "";
  if (/^[a-z0-9]+(-[a-z0-9]+)+$/i.test(s)) {
    return s
      .split("-")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
  }
  return s;
}

/** Primary label for the Type of detection column (plain string). */
export function getWafEventDetectionLabel(item) {
  const attackTypes = getWafEventAttackTypes(item);
  if (attackTypes.length > 0) {
    return attackTypes.map(formatWafAttackTypeLabel).join(", ");
  }

  const action = item?.action_taken;
  if (action) return String(action);
  if (item?.blocked === 1 || item?.blocked === true) return "Blocked";
  const sourceLabel = getWafSourceDisplayLabel(item?._src);
  if (sourceLabel) return sourceLabel;
  return "—";
}

/**
 * Structured display for the Type of detection table column.
 * Multiple attack_types → title + blue subtitle; single → title only.
 */
export function getWafEventDetectionDisplay(item) {
  const attackTypes = getWafEventAttackTypes(item).map(formatWafAttackTypeLabel);

  if (attackTypes.length > 1) {
    return {
      mode: "multiple",
      title: "Multiple Attack Detected",
      subtitle: attackTypes.join(", "),
    };
  }
  if (attackTypes.length === 1) {
    return { mode: "single", title: attackTypes[0] };
  }

  const fallback = getWafEventDetectionLabel(item);
  if (fallback === "—") {
    return { mode: "empty", title: "—" };
  }
  return { mode: "single", title: fallback };
}

/** Resolve filter dropdown values from strict list API fields. */
export function getWafEventFilterValue(event, filterId) {
  if (!event) return undefined;
  switch (filterId) {
    case "id":
    case "request_id":
      return event.event_id || event.request_id || event.id;
    case "src_ip":
      return event.ip;
    case "hostname":
      return event.host;
    case "uri":
      return event.uri;
    case "status":
      return getWafEventStatus(event);
    case "attack_type": {
      const types = getWafEventAttackTypes(event);
      return types[0] || event.attack_type;
    }
    case "waf_type":
    case "source":
      return event.source || event._src;
    case "res_code":
    case "resStatus":
    case "res_status":
      return event.status ?? event.res_code ?? event.res_status ?? event.resStatus;
    case "ua":
    case "user_agent":
      return event.user_agent ?? event.ua;
    case "ja3_hash":
    case "ja3":
    case "fp":
      return event.fp ?? event.ja3_hash ?? event.ja3 ?? event?.attacker?.fingerprints?.ja3;
    default:
      return event[filterId];
  }
}

export function getWafEventRowKey(item, index) {
  return item?.request_id || `waf_row_${index}`;
}

/** List row source: signature_waf vs smart_parse (default). */
export function getWafEventListSource(item) {
  return item?._src === "signature_waf" ? "signature_waf" : "smart_parse";
}

/** GET log/waf_event/:id query param from list row _src. */
export function getWafEventDetailFilter(item) {
  return item?._src === "signature_waf" ? "signature_waf" : "smartparse_waf";
}

/** Ensure POST log/waf_event always receives a valid time_range object. */
export function normalizeWafEventTimeRange(timeRange) {
  if (!timeRange || typeof timeRange !== "object") {
    return { period: "7d" };
  }
  if (typeof timeRange.period === "string" && timeRange.period.trim()) {
    return { period: timeRange.period.trim() };
  }
  if (
    typeof timeRange.time_zone === "string" &&
    typeof timeRange.from === "string" &&
    typeof timeRange.to === "string" &&
    timeRange.from.trim() &&
    timeRange.to.trim()
  ) {
    return {
      time_zone: timeRange.time_zone,
      from: timeRange.from,
      to: timeRange.to,
    };
  }
  return { period: "7d" };
}
